<template>
  <div class="f-v card surface clipped profile-card-container">
    <div class="pos-abs right-16 top-16 z-10 f f-gap-10">
      <button v-if="isCurrentUser" v-tooltip="'Choose profile banner'" class="edit-profile-button darker-gray on-white pill" @click="openProfileBannerModal">
        <i class="mi md-18 white bold">image</i>
      </button>
      <button v-if="isCurrentUser" class="edit-profile-button darker-gray on-white pill" @click="openProfileInfoModal">
        <i class="mi md-18 white bold">edit</i>
      </button>
    </div>
    <div class="about-card-banner">
      <donut-soft-load-img>
        <cdn-img :path="currentProfileBannerImg" class="w-100p d-b" style="aspect-ratio: 231 / 36" />
      </donut-soft-load-img>
      <div class="social-icons-container">
        <div class="f-h">
          <a class="c-p" :class="{ 'm-rx2': linkedinEntry.hasEntry }" :href="`https://slack.com/app_redirect?team=${profileUserTeamUid}&channel=${profileUser.uid}`" target="_blank">
            <cdn-svg path="nucleo/slack" :version="2" class="h-x3" />
          </a>
          <a v-if="linkedinEntry.hasEntry" class="c-p" :href="linkedinEntry.url" target="_blank">
            <cdn-svg path="nucleo/linkedin" :version="0" class="h-x3 blue linkedin-icon" />
          </a>
          <a v-if="profileUser.email" class="c-p" :href="`mailto:${profileUser.email}`" target="_blank"><lucide-icon name="Mail" :size="24" :stroke-width="2" /></a>
        </div>
      </div>
    </div>
    <div class="f-h f-v f-gap-12 f-as">
      <div class="f-h f-sb w-100p f-ae profile-image-container">
        <img :src="profileUser.avatar" alt="user avatar" height="96px" width="96px" class="round avatar-white-border">
      </div>
      <div class="f-h f-sb w-100p">
        <div class="f-h f-gap-12 f-ab w-100p">
          <p class="t-xxl demibold">{{ profileUser.name }}</p>
          <p class="t-lg gray" :class="{ 'ital': !profileUser.email }">{{ profileUser.email || 'email' }}</p>
        </div>
      </div>
      <div class="f-v f-gap-4 w-100p">
        <div class="f-h f-sb w-100p">
          <div class="f-h f-gap-8 f-ac">
            <p v-if="jobTitleEntry.hasEntry" class="t-lg medium">{{ jobTitleEntry.text }}</p>
            <div v-if="jobTitleEntry.hasEntry && teamEntry.hasEntry" class="w-3 h-3 bg-black round" />
            <p v-if="teamEntry.hasEntry" class="t-lg medium">{{ teamEntry.text }}</p>
          </div>
          <div v-if="managerEntry.hasEntry" class="f-h f-ac">
            <p class="t-lg medium">Reports to&nbsp;</p>
            <span class="t-lg bold">{{ managerEntry.text }}</span>
          </div>
        </div>
        <div class="f-h f-sb w-100p">
          <p class="t-ml gray medium">
            <template v-if="getTimeEntry().hasEntry">{{ getTimeEntry().text }}</template>
            <template v-if="getTimeEntry().hasEntry && locationEntry.hasEntry">, </template>
            <template v-if="locationEntry.hasEntry">
              <span>{{ locationEntry.text }}</span>
            </template>
          </p>
          <p v-if="startDateEntry.hasEntry" class="t-ml gray medium">At {{ companyName }} since {{ startDateEntry.text }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useStore } from 'vuex';

const { state, getters, commit } = useStore();

const profileUser = computed(() => getters['userProfileFields/profileUser']);
const bannersPaths = computed(() => state.userProfileFields.bannersPaths);
const profileEntries = computed(() => getters['userProfileFields/profileEntries']);
const currentProfileBannerId = computed(() => getters['userProfileFields/currentProfileBannerId']);
const currentProfileBannerImg = computed(() => getters['userProfileFields/currentProfileBannerImg']);
const profileUserTeamUid = computed(() => getters['userProfileFields/profileUserTeamUid']);
const isCurrentUser = computed(() => state.currentUser.id === profileUser.value?.id);

// Function to open profile info edit modal
const openProfileInfoModal = () => {
  const profileCategoryMachineNames = getters['userProfileFields/profileCategoryMachineNames'];
  commit('updateModal', {
    visible: true,
    size: 'xxl h-90vh of-x-h',
    subcomponent: 'user-profiles-modals-edit-profile',
    dismissable: false,
    data: {
      profileUser: profileUser.value,
      profileEntries: profileEntries.value,
      profileCategoryMachineNames: profileCategoryMachineNames || [],
    },
  });
};

const openProfileBannerModal = () => {
  commit('updateModal', {
    visible: true,
    size: 'xl of-x-h',
    subcomponent: 'user-profiles-modals-edit-banner',
    data: {
      bannersPaths: bannersPaths.value,
      value: currentProfileBannerId.value,
    },
  });
};

const currentProfileUserTime = ref('');

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const formatProfileUserCurrentTime = () => {
  if (!profileUser.value.time_zone_info?.iana) return '';
  const now = new Date();
  return now.toLocaleTimeString('en-US', {
    timeZone: profileUser.value.time_zone_info.iana,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const updateTime = () => {
  currentProfileUserTime.value = formatProfileUserCurrentTime();
};

const timeInterval = ref(null);

onMounted(() => {
  updateTime();
  timeInterval.value = setInterval(updateTime, 1000);
});

onUnmounted(() => {
  if (timeInterval.value) clearInterval(timeInterval.value);
});

const profileManager = computed(() => {
  const managers = getters['userProfileFields/profileManagers'];
  return managers && managers.length > 0 ? managers[0] : null;
});

const profileEntry = (categoryName, entryName) => {
  const category = profileEntries.value.find(entry => entry.category.machine_name === categoryName);
  if (!category) return null;
  return category.entries.find(entry => entry.field.machine_name === entryName)?.text;
};

// Individual computed properties for profile entries
const locationEntry = computed(() => ({
  text: profileEntry('my_profile', 'location'),
  hasEntry: !!profileEntry('my_profile', 'location'),
  default: 'Location',
}));

const getTimezoneAbbreviation = () => {
  if (!profileUser.value.time_zone_info?.label) return '';
  // Extract abbreviation from label like "Mountain Time (US & Canada) (MST/MDT)" -> "MST"
  const match = profileUser.value.time_zone_info.label.match(/\(([A-Z]+)\//);
  return match ? match[1] : '';
};

const getTimeEntry = () => {
  const timezone = getTimezoneAbbreviation();
  return {
    text: `${currentProfileUserTime.value}${timezone ? ` ${timezone}` : ''}`,
    hasEntry: !!currentProfileUserTime.value,
  };
};

const jobTitleEntry = computed(() => ({
  text: profileUser.value.job_title,
  hasEntry: !!profileUser.value.job_title,
  default: 'Job title',
}));

const teamEntry = computed(() => ({
  text: profileEntry('my_profile', 'team'),
  hasEntry: !!profileEntry('my_profile', 'team'),
  default: 'Team',
}));

const startDateEntry = computed(() => ({
  text: profileEntry('my_profile', 'start_date') ? formatDate(profileEntry('my_profile', 'start_date')) : '',
  hasEntry: !!profileEntry('my_profile', 'start_date'),
  default: 'Start date',
}));

const managerEntry = computed(() => ({
  text: profileManager.value?.related_user?.name,
  hasEntry: !!profileManager.value?.related_user?.name,
  default: 'Manager',
}));

const linkedinEntry = computed(() => ({
  text: profileEntry('my_profile', 'linkedin'),
  hasEntry: !!profileEntry('my_profile', 'linkedin'),
  isLink: true,
  url: profileEntry('my_profile', 'linkedin'),
  default: 'LinkedIn',
}));

const companyName = computed(() => state.currentTeam.name || 'Company');
</script>

<style scoped>
.profile-card-container {
  position: relative;
}

.profile-card-buttons {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
}

.edit-profile-button {
  background: white;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
}

.edit-profile-button:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.about-card-banner {
  position: relative;
  overflow: visible;
  margin-top: -24px;
  margin-left: -24px;
  margin-right: -24px;
  width: calc(100% + 48px);
}

.social-icons-container {
  position: absolute;
  top: 100%;
  margin-top: 8px;
  right: 24px;
  z-index: 10;
}

.profile-image-container {
  position: relative;
  z-index: 5;
  margin-top: -48px;
}

.avatar-white-border {
  border: 3px solid #fff;
}

.about-card-banner > :deep(svg) {
  display: block;
  width: 100%;
  height: auto;
  /* allow clicks to pass through to icons */
  pointer-events: none;
}

/* hacky way to make the nucleo linkedin icon blue */
.linkedin-icon {
  filter: invert(29%) sepia(97%) saturate(1337%) hue-rotate(192deg) brightness(76%) contrast(96%);
}
</style>
